import {
  Avatar,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { ClearRounded, UpdateRounded } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { ProjectWorkflow } from '../../api/project-workflows';
import { RootState } from '../../app/store';
import HelpTooltip from '../../components/HelpTooltip';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import mapExistingUserInWorkflowStep from '../../functions/mapExistingUserInWorkflowStep';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useProjectUsers from '../../hooks/useProjectUsers';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { getProjectAsync } from '../Project/projectSlice';
import { User } from '../UserProfile/userProfileSlice';
import {
  getProjectWorkflowAsync,
  updateProjectWorkflowAsync
} from './projectWorkflowSlice';

export default function WorkflowEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const users = useProjectUsers();
  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );
  const { data: workflowSteps } = useSelector(
    (state: RootState) => state.workflowSteps
  );
  const { selectedProjectWorkflow } = useSelector(
    (state: RootState) => state.projectWorkflows
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const params = useParams<{ flowId: string }>();
  const flowId = Number.parseInt(params.flowId, 10);
  const projectId = selectedProjectWorkflow?.projectId;

  const [projectWorkflow, setProjectWorkflow] = useState<ProjectWorkflow>({
    name: '',
    discipline: null,
    isCreatorViewOnlyExternal: false,
    isCreatorViewOnlyFinal: false,
    projectWorkflowSteps: []
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (selectedProjectWorkflow) {
      const projectWorkflowSteps = workflowSteps.map((workflowStep) => {
        const projectWorkflowStep =
          selectedProjectWorkflow.projectWorkflowSteps.find(
            (projectWorkflowStep) =>
              workflowStep.id === projectWorkflowStep.workflowStepId
          );

        let projectApprovalWorkflowStepUsers: User[] = [];
        if (projectWorkflowStep) {
          projectApprovalWorkflowStepUsers = mapExistingUserInWorkflowStep(
            projectWorkflowStep.projectApprovalWorkflowSteps,
            users
          );
        }
        return {
          ...workflowStep,
          isRequired: projectWorkflowStep ? true : false,
          users: projectApprovalWorkflowStepUsers
        };
      });
      setProjectWorkflow({
        ...projectWorkflow,
        name: selectedProjectWorkflow.name,
        discipline:
          disciplines.find(
            (discipline) =>
              selectedProjectWorkflow.disciplineId === discipline.id
          ) ?? null,
        isCreatorViewOnlyExternal:
          selectedProjectWorkflow.isCreatorViewOnlyExternal,
        isCreatorViewOnlyFinal: selectedProjectWorkflow.isCreatorViewOnlyFinal,
        projectWorkflowSteps
      });
    }
  }, [selectedProjectWorkflow, workflowSteps, users]);

  useEffect(() => {
    if (selectedProjectWorkflow) {
      dispatch(getProjectAsync(selectedProjectWorkflow.projectId));
    }
  }, [selectedProjectWorkflow]);

  useEffect(() => {
    dispatch(getProjectWorkflowAsync(flowId));
  }, []);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (selectedProject && flowId) {
        const result = await dispatch(
          updateProjectWorkflowAsync({
            project: selectedProject,
            projectWorkflow: { ...projectWorkflow, id: flowId }
          })
        );
        await unwrapResult(result);
        dispatch(
          loadingSlice.actions.success({
            message: t('projectWorkflowEdit.messages.workflowUpdated') as string
          })
        );
        setTimeout(() => {
          history.push(`/projects/${projectId}?tabId=1`);
        }, 1000);
      } else {
        throw new Error(t('projectWorkflowEdit.errors.pleaseSelectProject') as string);
      }
    } catch (error) {
      setLoading(false);
    }
  }

  function onChangeByField(
    projectWorkflowStepId: number,
    newValue: boolean | User[],
    fieldName: string
  ) {
    const { projectWorkflowSteps } = projectWorkflow;
    const projectWorkflowStepIndex = projectWorkflowSteps.findIndex(
      (projectWorkflowStep) => projectWorkflowStep.id === projectWorkflowStepId
    );
    const newProjectWorkflowStep = {
      ...projectWorkflowSteps[projectWorkflowStepIndex],
      [fieldName]: newValue
    };
    const newProjectWorkflowSteps = [
      ...projectWorkflowSteps.slice(0, projectWorkflowStepIndex),
      newProjectWorkflowStep,
      ...projectWorkflowSteps.slice(projectWorkflowStepIndex + 1)
    ];
    setProjectWorkflow({
      ...projectWorkflow,
      projectWorkflowSteps: newProjectWorkflowSteps
    });
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="editProjectWorkflow"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          projectWorkflowName: selectedProjectWorkflow?.name,
          projectWorkflowId: selectedProjectWorkflow?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectWorkflowEdit.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
        </Grid>
        <Grid item xs={12} md={9}>
          <form onSubmit={onSubmit}>
            <Paper className={classes.card}>
              <Typography variant="subtitle1" gutterBottom>
                {t('projectWorkflowEdit.sections.workflow')}
                <HelpTooltip>
                  {t('projectWorkflowEdit.helpTexts.flowForApprovalDocs')}
                </HelpTooltip>
              </Typography>
              <Divider className={classes.divider} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    className={classes.fullWidth}
                    id="flow-name"
                    variant="outlined"
                    size="small"
                    label={t('projectWorkflowEdit.labels.flowName')}
                    value={projectWorkflow.name}
                    onChange={(event) => {
                      setProjectWorkflow({
                        ...projectWorkflow,
                        name: event.target.value
                      });
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="discipline"
                    options={disciplines}
                    getOptionLabel={(option) => option?.name}
                    value={projectWorkflow.discipline}
                    getOptionSelected={(option, value) =>
                      option.id === value?.id
                    }
                    onChange={(event, newValue) => {
                      setProjectWorkflow({
                        ...projectWorkflow,
                        discipline: newValue
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('projectWorkflowEdit.labels.discipline')}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormLabel component="legend">
                    {t('projectWorkflowEdit.sections.creatorConfiguration')}
                  </FormLabel>
                  <div>
                    <FormControlLabel
                      className={classes.customizeCreator}
                      control={
                        <Switch
                          checked={projectWorkflow.isCreatorViewOnlyExternal}
                          onChange={(event) =>
                            setProjectWorkflow({
                              ...projectWorkflow,
                              isCreatorViewOnlyExternal: event.target.checked
                            })
                          }
                          name="isCreatorViewOnlyExternal"
                          color="primary"
                        />
                      }
                      label={t('projectWorkflowEdit.labels.showExternalStepOnly')}
                    />
                    <HelpTooltip>
                      {t('projectWorkflowEdit.helpTexts.creatorCanSeeExternal')}{' '}
                      {workflowSteps
                        .filter((workflowStep) => !workflowStep.isInternal)
                        .map((workflowStep) => workflowStep.name)
                        .join(', ')}
                    </HelpTooltip>
                  </div>
                  <div>
                    <FormControlLabel
                      className={classes.customizeCreator}
                      control={
                        <Switch
                          checked={projectWorkflow.isCreatorViewOnlyFinal}
                          onChange={(event) =>
                            setProjectWorkflow({
                              ...projectWorkflow,
                              isCreatorViewOnlyFinal: event.target.checked
                            })
                          }
                          name="isCreatorViewOnlyFinal"
                          color="primary"
                        />
                      }
                      label={t('projectWorkflowEdit.labels.showApprovalCodeOnly')}
                    />
                    <HelpTooltip>
                      {t('projectWorkflowEdit.helpTexts.creatorCanSeeFinal')}{' '}
                      {workflowSteps
                        .filter((workflowStep) => workflowStep.isFinalApprover)
                        .map((workflowStep) => workflowStep.name)
                        .join(', ')}
                    </HelpTooltip>
                  </div>
                  {(projectWorkflow.isCreatorViewOnlyExternal ||
                    projectWorkflow.isCreatorViewOnlyFinal) && (
                    <FormHelperText>
                      {t('projectWorkflowEdit.helpTexts.theCreatorCanSeeOnly')}{' '}
                      {
                        <b>
                          {workflowSteps
                            .filter(
                              ({ isInternal, isFinalApprover }) =>
                                (!projectWorkflow.isCreatorViewOnlyExternal ||
                                  !isInternal) &&
                                (!projectWorkflow.isCreatorViewOnlyFinal ||
                                  isFinalApprover)
                            )
                            .map((workflowStep) => workflowStep.name)
                            .join(', ')}
                        </b>
                      }
                    </FormHelperText>
                  )}
                </Grid>
              </Grid>
            </Paper>
            <Paper className={classes.card}>
              <Typography variant="subtitle1" gutterBottom>
                {t('projectWorkflowEdit.sections.reviewers')}
                <HelpTooltip>
                  {t('projectWorkflowEdit.helpTexts.documentApprovers')}
                </HelpTooltip>
              </Typography>
              <Divider className={classes.divider} />
              {projectWorkflow.projectWorkflowSteps.map(
                (projectWorkflowStep, index) => {
                  return (
                    <div key={projectWorkflowStep.id}>
                      <div className={classes.flexFlowStep}>
                        <Avatar
                          className={clsx(classes.smallAvatar, {
                            [classes.avatarDisabled]:
                              !projectWorkflowStep.isRequired
                          })}
                        >
                          {index + 1}
                        </Avatar>
                        <Typography
                          className={classes.flexGrowName}
                          variant="subtitle1"
                          color={
                            projectWorkflowStep.isRequired
                              ? 'textPrimary'
                              : 'textSecondary'
                          }
                        >
                          {projectWorkflowStep.name}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={projectWorkflowStep?.isRequired}
                              onChange={(event) =>
                                onChangeByField(
                                  projectWorkflowStep.id,
                                  event.target.checked,
                                  'isRequired'
                                )
                              }
                              name={`${projectWorkflowStep.id}`}
                              color="primary"
                            />
                          }
                          label={t('projectWorkflowEdit.labels.required')}
                        />
                      </div>
                      <Collapse in={projectWorkflowStep.isRequired}>
                        <Autocomplete
                          className={classes.userField}
                          id={`userId-${projectWorkflowStep.id}`}
                          size="small"
                          multiple
                          options={users}
                          getOptionLabel={(option) =>
                            `${option?.firstName} ${option?.lastName}`
                          }
                          renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                              <Chip
                                label={`${option?.firstName} ${option?.lastName}`}
                                {...getTagProps({ index })}
                                variant="outlined"
                                size="small"
                                deleteIcon={<ClearRounded />}
                              />
                            ))
                          }
                          value={projectWorkflowStep.users}
                          getOptionSelected={(option, value) =>
                            option.id === value?.id
                          }
                          onChange={(event, newValue) =>
                            onChangeByField(
                              projectWorkflowStep.id,
                              newValue,
                              'users'
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={
                                projectWorkflowStep.users.length === 0
                                  ? (t('projectWorkflowEdit.placeholders.selectUser') as string)
                                  : ''
                              }
                              required={
                                projectWorkflowStep.isRequired &&
                                projectWorkflowStep.users.length === 0
                              }
                            />
                          )}
                        />
                      </Collapse>
                    </div>
                  );
                }
              )}
            </Paper>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={<UpdateRounded />}
            >
              {t('projectWorkflowEdit.buttons.updateProjectWorkflow')}
            </Button>
          </form>
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    fullWidth: {
      width: '100%'
    },
    smallAvatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      backgroundColor: theme.palette.primary.main
    },
    avatarDisabled: {
      backgroundColor: theme.palette.text.disabled
    },
    flexFlowStep: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(2, 0)
    },
    flexGrowName: {
      flexGrow: 1,
      padding: theme.spacing(0, 2)
    },
    userField: {
      width: `calc(100% - ${theme.spacing(8)}px)`,
      marginLeft: theme.spacing(6)
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    customizeCreator: {
      marginRight: theme.spacing(0)
    }
  })
);
